import faker from 'faker';
import React from 'react';
import { useInterval } from 'usehooks-ts';

import { Task } from '@pitaman71/omniglot-code-instruments';
import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Debug from './Debug';
import { Clients, Objects, ProposeZone, Properties, Relations, Stores, Streams } from '@pitaman71/omniglot-live-data';

import * as Database from './Database';
import * as Expression from './Expressions';

const __moduleName__ = 'omniglot-live-react/Propose';

export function withProposal(
    prefix: string, 
    _zone: Stores.Zone, 
    pollInterval: { ms: number },
    render: (zone_: Stores.Zone) => JSX.Element
): JSX.Element {
    const [ peerId ] = React.useState(() => prefix+'-'+faker.datatype.uuid().slice(0,7));
    const [ zone, ] = React.useState(() => {
        const zone_ = new ProposeZone.ProposeZone(peerId, _zone);
        return zone_;
    });
    useInterval(() => {
        new Task.Task(`update proposal ${peerId}`).logs(console.log).promises(undefined, () => (
            Promise.all([ 
                ...zone.streams().properties().map(stream => {
                    stream.scalar?.pull ? stream.scalar.pull() :
                    stream.set?.pull ? stream.set.pull() :
                    stream.sequence?.pull ? stream.sequence.pull() :
                    stream.map?.pull ? stream.map.pull() : Promise.resolve()
                }),
                ...zone.streams().relations().map(stream => {
                    stream.pull ? stream.pull() : Promise.resolve();
                })
            ])
        ))
    }, pollInterval.ms)
    return (
        <Debug.Boundary name={`Zone ${peerId}`}>
            { render(zone) }
        </Debug.Boundary>
    );
}

export function withRelations(
    zone: Stores.Zone,
    relations: {
        [key: string]: () => Relations.Anchor<any>
    },
    render: (streams: {
        [key: string]: Streams.RelationStream<any>
    }) => JSX.Element,
    streams_: {
        [key: string]: Streams.RelationStream<any>
    } = {}
): JSX.Element {
    const relationSymbols = Object.getOwnPropertyNames(relations);
    if(relationSymbols.length === 0)
        return (
            <Debug.Boundary name={`Propose.browseRelations calls render ${Object.getOwnPropertyNames(streams_)}`}>
                { 
                    render(streams_)
                }
            </Debug.Boundary>
        )
    else {
        return _withRelations(zone, relations, render, streams_);
    }
}

function _withRelations<PatternBindingType extends { [symbol: string]: Objects.Binding<string> }>(
    zone: Stores.Zone,
    relations: {
        [key: string]: () => Relations.Instance<any>
    },
    render: (streams: {
        [key: string]: Streams.RelationStream<any>
    }) => JSX.Element,
    streams_: {
        [key: string]: Streams.RelationStream<any>
    } = {}
): JSX.Element {
    const relationSymbols = Object.getOwnPropertyNames(relations);
    const [ stream, ] = React.useState(() => {
        const instance = relations[relationSymbols[0]]();
        return zone.streams().relation(instance);
    });

    const nextRelations = { ... relations };
    delete nextRelations[relationSymbols[0]];
    return (
        <Debug.Boundary name={`Propose._withRelations select is not false for ${JSON.stringify(streams_)}`}>
            {
                withRelations(zone, nextRelations, render, { ... streams_, [relationSymbols[0]]: stream })
            }
        </Debug.Boundary>
    );
}

export function browse(
    streams: {
        [key: string]: Streams.RelationStream<any>
    }, 
    select: (relations: {
        [key: string]: Objects.BindingType
    }) => Expression.Operand<boolean>,
    render: (
        relations: {
            [key: string]: Objects.BindingType
        }
    ) => JSX.Element,    
    relations_: {
        [key: string]: Objects.BindingType
    } = {}
): JSX.Element {
    const symbols = Object.getOwnPropertyNames(streams);
    if(symbols.length === 0) {
        return (
            <Debug.Boundary name={`Propose.browse calls render${JSON.stringify(relations_)}`}>
                { render(relations_) }
            </Debug.Boundary>
        )
    }
    return _browse(streams, select, render, relations_);
}

export function _browse(
    streams: {
        [key: string]: Streams.RelationStream<any>
    }, 
    select: (relations: {
        [key: string]: Objects.BindingType
    }) => Expression.Operand<boolean>,
    render: (
        relations: {
            [key: string]: Objects.BindingType
        }
    ) => JSX.Element,    
    relations_: {
        [key: string]: Objects.BindingType
    } = {}    
): JSX.Element {
    const symbols = Object.getOwnPropertyNames(streams);
    const stream = streams[symbols[0]];
    const [ entries, setEntries ] = React.useState<Objects.BindingType[]>([]);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true })
    const client = Clients.RelationClient.fromDelta<Objects.BindingType>(JSON.stringify(streams[symbols[0]]), setEntries, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        setSubscription(client.watchParent(stream));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ ]);

    return (
        <Debug.Boundary name={`Propose._browse iterates over ${entries.length} entries in ${JSON.stringify(symbols)}`}>
            {
                entries
                .filter(entry => {
                    const nextRelations = { ... relations_ };
                    nextRelations[symbols[0]] = entry;
                    return select(nextRelations).isAssigned() !== false;
                })
                .map(entry => {
                    const nextStreams = {... streams };
                    delete nextStreams[symbols[0]];
                    const nextRelations = { ... relations_ };
                    nextRelations[symbols[0]] = entry;
                    return (
                        <Debug.Boundary key={JSON.stringify(entry)} name={`Propose._browse calls render(${symbols[0]} : ${JSON.stringify(entry)})`}>
                            { browse(nextStreams, select, render, nextRelations) }
                        </Debug.Boundary>
                    )
                })
            }
        </Debug.Boundary>
    )
}

export function add(
    streams: {
        [key: string]: Streams.RelationStream<any>
    }, 
    select: (relations: {
        [key: string]: Objects.BindingType
    }) => Expression.Operand<boolean>,
    render: (
        relations: {
            [key: string]: Objects.BindingType
        }
    ) => JSX.Element,    
    relations_: {
        [key: string]: Objects.BindingType
    } = {}
): JSX.Element {
    const symbols = Object.getOwnPropertyNames(streams);
    if(symbols.length === 0) {
        return (
            <Debug.Boundary name={`add calls render`}>
                { render(relations_) }
            </Debug.Boundary>
        )
    }
    return _add(streams, select, render, relations_);
}


export function _add(
    streams: {
        [key: string]: Streams.RelationStream<any>
    }, 
    select: (relations: {
        [key: string]: Objects.BindingType
    }) => Expression.Operand<boolean>,
    render: (
        relations: {
            [key: string]: Objects.BindingType
        }
    ) => JSX.Element,    
    relations_: {
        [key: string]: Objects.BindingType
    } = {}
): JSX.Element {
    const symbols = Object.getOwnPropertyNames(streams);
    const stream = streams[symbols[0]];
    const [ spawned, setSpawned ] = React.useState<undefined|Objects.BindingType>();
    React.useEffect(() => {
        const stateful = stream.stateful();
        if(stateful) {
            const assignable = { ... relations_, [symbols[0]]: stream.anchor.binding}
            select(assignable).doAssign(true);
            const spawned = stream.anchor.descriptor.spawn(assignable[symbols[0]]);
            if(spawned !== assignable[symbols[0]]) {
                stateful.insert(spawned);
                setSpawned(spawned);
            }
        }
        return () => {
            const stateful = stream.stateful();
            if(stateful && spawned !== undefined) {
                stateful.revert();
            }
        }
    }, symbols)
    const nextStreams = { ... streams };
    delete nextStreams[symbols[0]];
    const nextRelations = { ... relations_, [symbols[0]]: spawned || stream.anchor.binding};
    return (
        <Debug.Boundary name={`add spawns ${symbols[0]}: (${JSON.stringify(spawned || stream.anchor)})`}>
            { add(nextStreams, select, render, nextRelations) }
        </Debug.Boundary>
    )
}

export function withRelation<RelationTypeParams extends Objects.BindingType>(
    relation: () => Relations.Anchor<RelationTypeParams>,
    zone: Stores.Zone,
    render: (
        values: RelationTypeParams[],
        client?: Clients.RelationClient<RelationTypeParams>
    ) => JSX.Element
): JSX.Element {
    const [ stream, ] = React.useState(() => {
        const anchor = relation();
        return zone.streams().relation(anchor);
    });
    return <RelationEntries stream={stream} render={render}/>;
}

export function RelationEntries<RelationTypeParams extends Objects.BindingType>(props: {
    stream: Streams.RelationStream<RelationTypeParams>,
    render: (
        values: RelationTypeParams[],
        client?: Clients.RelationClient<RelationTypeParams>
    ) => JSX.Element
}): JSX.Element {
    const [ value, setValue ] = React.useState<RelationTypeParams[]>([]);
    const client = Clients.RelationClient.fromDelta(`${__moduleName__}/RelationEntries_(${props.stream})`, setValue);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        setSubscription(client.watchParent(props.stream));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ props.stream ]);
    const entries: RelationTypeParams[] = [];
    const seen = new Set<string>();

    value.forEach(entry => {
        const keys = [... Object.keys(entry)];
        keys.sort();
        const asString = keys.reduce((_asString, key) => (
            !entry[key].objectId ? _asString : (_asString === "" ? "" : _asString+',')+key+"="+entry[key].objectId
        ), "");
        if(!seen.has(asString)) {
            seen.add(asString);
            entries.push(entry)
        }
    })
    return (
        <React.Fragment>
            { props.render(entries, props.stream.stateful()) }
        </React.Fragment>
    );
}

export function withProperty<PropertyTypeParams extends Properties.TypeParams>(
    property: () => Properties.Instance<any>,
    zone: Stores.Zone,
    render: {
        scalar?: (
            value: PropertyTypeParams["Value"], 
            client?: Clients.ScalarClient<PropertyTypeParams["Value"]>
        ) => JSX.Element,
        set?: (
            value: Set<PropertyTypeParams["Value"]>, 
            client?: Clients.SetClient<PropertyTypeParams["Value"]>
        ) => JSX.Element,
        sequence?: (
            value: Array<PropertyTypeParams["Value"]>, 
            client?: Clients.SequenceClient<PropertyTypeParams["Value"]>
        ) => JSX.Element,
    }
): JSX.Element {
    const [ stream, ] = React.useState(() => {
        const instance = property();
        return zone.streams().property(instance);
    });
    return withPropertyData(stream, render);
}

export interface PropertyStreams<PropertyTypeParams extends Properties.TypeParams> {
    scalar?: (
        stream: Streams.ScalarStream<PropertyTypeParams["Value"]>
    ) => JSX.Element,
    set?: (
        stream: Streams.SetStream<PropertyTypeParams["Value"]>
    ) => JSX.Element,
    sequence?: (
        stream: Streams.SequenceStream<PropertyTypeParams["Value"]>
    ) => JSX.Element,
}

export function withPropertyStream<PropertyTypeParams extends Properties.TypeParams>(
    property: () => Properties.Instance<any>,
    zone: Stores.Zone,
    render: PropertyStreams<PropertyTypeParams>
): JSX.Element {
    const [ stream, ] = React.useState(() => {
        const instance = property();
        return zone.streams().property(instance);
    });
    if(stream.scalar && render.scalar) return render.scalar(stream.scalar);
    if(stream.set && render.set) return render.set(stream.set);
    if(stream.sequence && render.sequence) return render.sequence(stream.sequence);
    return <React.Fragment></React.Fragment>;
}

export interface PropertyClients<PropertyTypeParams extends Properties.TypeParams> {
    scalar?: (
        value: PropertyTypeParams["Value"], 
        client?: Clients.ScalarClient<PropertyTypeParams["Value"]>
    ) => JSX.Element,
    set?: (
        value: Set<PropertyTypeParams["Value"]>, 
        client?: Clients.SetClient<PropertyTypeParams["Value"]>
    ) => JSX.Element,
    sequence?: (
        value: Array<PropertyTypeParams["Value"]>, 
        client?: Clients.SequenceClient<PropertyTypeParams["Value"]>
    ) => JSX.Element,
}

export function withPropertyData<PropertyTypeParams extends Properties.TypeParams>(
    stream: Streams.PropertyStream<PropertyTypeParams>,
    render: PropertyClients<PropertyTypeParams>
) {
    if(stream.scalar && render.scalar) return <ScalarData stream={stream.scalar} render={render.scalar}/>;
    if(stream.set && render.set) return <SetData stream={stream.set} render={render.set}/>;
    if(stream.sequence && render.sequence) return <SequenceData stream={stream.sequence} render={render.sequence}/>;
    return (
        <Debug.Boundary name="withPropertyData has miconfigured stream">
            {[]}
        </Debug.Boundary>
    )
}

export function PropertyData<PropertyTypeParams extends Properties.TypeParams>(props: {
    stream: Streams.PropertyStream<PropertyTypeParams>,
    render: PropertyClients<PropertyTypeParams>
}) {
    return withPropertyData(props.stream, props.render);
}

export function ScalarData<PropertyTypeParams extends Properties.TypeParams>(props: {
    stream: Streams.ScalarStream<PropertyTypeParams>,
    render: (
        value: PropertyTypeParams["Value"], 
        client?: Clients.ScalarClient<PropertyTypeParams["Value"]>
    ) => JSX.Element
}): JSX.Element {
    const [ value, setValue ] = React.useState<PropertyTypeParams["Value"]|undefined>(undefined);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true });
    const client = Clients.ScalarClient.fromDelta<PropertyTypeParams["Value"], Elevated.Domain<PropertyTypeParams["Value"]>>(`${__moduleName__}/withScalarData(${props.stream})`, setValue, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        setSubscription(client.watchParent(props.stream));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ ]);
    return props.render(value, props.stream.stateful());
}

export function SetData<PropertyTypeParams extends Properties.TypeParams>(props: {
    stream: Streams.SetStream<PropertyTypeParams>,
    render: (
        value: Set<PropertyTypeParams["Value"]>, 
        client?: Clients.SetClient<PropertyTypeParams["Value"]>
    ) => JSX.Element
}): JSX.Element {
    const [ value, setValue ] = React.useState<Set<PropertyTypeParams["Value"]>>(new Set());
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true });
    const client = Clients.SetClient.fromDelta<PropertyTypeParams["Value"], Elevated.Domain<PropertyTypeParams["Value"]>>(`${__moduleName__}/withSetData(${props.stream})`, setValue, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        setSubscription(client.watchParent(props.stream));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ ]);
    return props.render(value, props.stream.stateful());
}

export function SequenceData<PropertyTypeParams extends Properties.TypeParams>(props: {
    stream: Streams.SequenceStream<PropertyTypeParams>,
    render: (
        value: PropertyTypeParams["Value"][], 
        client?: Clients.SequenceClient<PropertyTypeParams["Value"]>
    ) => JSX.Element
}): JSX.Element {
    const [ value, setValue ] = React.useState<Array<PropertyTypeParams["Value"]>>([]);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true });
    const client = Clients.SequenceClient.fromDelta<PropertyTypeParams["Value"], Elevated.Domain<PropertyTypeParams["Value"]>>(`${__moduleName__}/withSequenceData(${props.stream})`, setValue, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        setSubscription(client.watchParent(props.stream));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ ]);
    return props.render(value, props.stream.stateful());
}

export type Sortable = number | string | null | undefined | Sortable[];

function _compare(a: Sortable, b: Sortable): -1 | 1 | 0 {
    const _boolean = (a:boolean,b: boolean): -1 | 1 | 0 | 2 => (
        a && b ? 0
        : a && !b ? -1
        : !a && b ? 1
        : 2
    );
    const _number = (a: number, b: number): -1 | 1 | 0 => (
        a < b ? -1
        : a > b ? 1
        : 0
    );
    const _string = (a: string, b: string): -1 | 1 | 0 => (
        a < b ? -1
        : a > b ? 1
        : 0
    );

    let code = _boolean(a === undefined, b === undefined);
    if(code !== 2) return code;
    code = _boolean(a === null, b === null);
    if(code !== 2) return code;
    if(Array.isArray(a) && Array.isArray(b)) {
        code = _number(a.length, b.length);
        if(code !== 0) return code;
        for(let index=0;index < a.length;index++) {
            code = _compare(a[index], b[index]);
            if(code !== 0) return code;
        }
    } else {
        if(!Array.isArray(a) && Array.isArray(b)) return -1;
        if(Array.isArray(a) && !Array.isArray(b)) return 1;
    }
    if(typeof a === 'number' && typeof b === 'number') {
        return _number(a,b);
    }
    if(typeof a === 'string' && typeof b === 'string') {
        return _string(a,b);
    }
    return 0;
}

interface _SortEntriesState<RelationTypeParams> {
    keyToEntry: Map<string, RelationTypeParams>,
    entryToKey: Map<string, Sortable>,
    order: Sortable[]
};

export function SortEntries<RelationTypeParams extends Objects.BindingType>(props: {
    unsorted: RelationTypeParams[],
    client?: Clients.RelationClient<RelationTypeParams>,
    compute: (
        unsorted: RelationTypeParams[],
        updateSortKey: (entry: RelationTypeParams, sortKey_: Sortable) => void
    ) => JSX.Element,
    render: (
        sorted: RelationTypeParams[],
        getSortKey: (entry: RelationTypeParams) => undefined|Sortable,
        client?: Clients.RelationClient<RelationTypeParams>
    ) => JSX.Element,
}): JSX.Element {
    const [ state, setState ] = React.useState<_SortEntriesState<RelationTypeParams>>({
        keyToEntry: new Map<string, RelationTypeParams>(),
        entryToKey: new Map<string, Sortable>(),
        order: []
    });
    const updateSortKey = (entry: RelationTypeParams, sortKey_: Sortable)=> {
        setState(_state => {
            const state = { 
                entryToKey: new Map(_state.entryToKey),
                keyToEntry: new Map(_state.keyToEntry),
                order: [ ... _state.order ]
            };
            const entryAsString = JSON.stringify(entry);
            const sortKeyAsString = JSON.stringify(sortKey_);
            let updated = false;
            const isNullKey = sortKey_ === undefined || sortKey_ === null;
            const isNewEntry = !state.entryToKey.has(entryAsString);
            if(isNullKey) {
                // if(isNewEntry)
                //     console.log(`NEW entry ${JSON.stringify(entry)} clearing sort key`);
                // else
                //     console.log(`SKIP entry ${JSON.stringify(entry)} key is NULL`);
            } else {
                if(isNewEntry) {
                    //console.log(`NEW entry ${JSON.stringify(entry)} setting sort key to ${sortKeyAsString}`);
                    updated = true;
                    state.entryToKey.set(entryAsString, sortKey_);
                    state.keyToEntry.set(sortKeyAsString, entry);
                    state.order.push(sortKey_);
                    state.order.sort(_compare);
                } else {
                    const _sortKey = state.entryToKey.get(entryAsString);
                    if(_compare(_sortKey, sortKey_) !== 0) {
                        //console.log(`UPDATE entry ${JSON.stringify(entry)} key ${JSON.stringify(_sortKey)} => ${sortKeyAsString}`);
                        updated = true;
                        state.entryToKey.set(entryAsString, sortKey_);
                        state.keyToEntry.delete(JSON.stringify(_sortKey));
                        state.keyToEntry.set(sortKeyAsString, entry);
                        state.order = state.order.filter(_key => _compare(_sortKey, _key) !== 0);
                        state.order.push(sortKey_);
                        state.order.sort(_compare);    
                    } else {
                        //console.log(`SKIP entry ${JSON.stringify(entry)} keeping curent sort key ${sortKeyAsString}`);
                    }
                }
                if(state.entryToKey.size != state.keyToEntry.size || state.entryToKey.size != state.order.length) {
                    throw `Sizes inconsistent: |entryToKey| = ${state.entryToKey.size} |keyToEntry| = ${state.keyToEntry.size} |order| = ${state.order.length}`
                }
            }
            return !updated ? _state : state;
        });
    };
    return <React.Fragment>
        {
            props.compute(props.unsorted, updateSortKey)
        }
        <_SortEntries {...props} state={state}/>
    </React.Fragment>;
}

function _SortEntries<RelationTypeParams extends Objects.BindingType>(props: {
    unsorted: RelationTypeParams[],
    client?: Clients.RelationClient<RelationTypeParams>,
    state: _SortEntriesState<RelationTypeParams>,
    render: (
        sorted: RelationTypeParams[],
        getSortKey: (entry: RelationTypeParams) => undefined|Sortable,
        client?: Clients.RelationClient<RelationTypeParams>
    ) => JSX.Element
}) {
    const unordered = props.unsorted.filter(entry => !props.state.entryToKey.has(JSON.stringify(entry)));
    const ordered = Array.from(props.state.order).map(key => props.state.keyToEntry.get(JSON.stringify(key))).filter((entry:undefined|RelationTypeParams) => !!entry && props.state.entryToKey.has(JSON.stringify(entry))) as RelationTypeParams[];
    const getSortKey = (entry: RelationTypeParams)=> {
        if(!props.state.entryToKey.has(JSON.stringify(entry))) return undefined;
        return props.state.entryToKey.get(JSON.stringify(entry));
    };

    return (
        <React.Fragment>
            { props.render([
                ...unordered,
                ...ordered
            ], getSortKey, props.client) }
        </React.Fragment>
    );
}

export function useScalarProperty<ValueType, DomainType extends Elevated.Domain<ValueType>>(
    stream: undefined|Streams.ScalarStream<ValueType, DomainType>
): {
    domain?: DomainType,
    value: undefined|ValueType, 
    status: Streams.Status,
    client?: Clients.ScalarClient<ValueType>
} {
    const { zone } = React.useContext(Database.Context);
    const [ value, setValue ] = React.useState<ValueType|undefined>(undefined);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true });
    const client = Clients.ScalarClient.fromDelta<ValueType, DomainType>(`${__moduleName__}/useScalarProperty(${stream})`, setValue, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
            setSubscription(undefined);
        }
        if(stream) {
            setStatus(_status => ({ ..._status, synchronizing: true }));
            setSubscription(client.watchParent(stream));
            stream.pull && stream.pull().then(() => {
                setStatus(_status => ({ ..._status, initializing: false, synchronizing: false }));
            })
        }
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ stream ]);
    return { domain: stream?.domain, value, status, client: stream?.stateful() };
}

export function useSetProperty<ValueType, DomainType extends Elevated.Domain<ValueType>>(
    stream: undefined|Streams.SetStream<ValueType, DomainType>
): {
    domain?: DomainType,
    values: Set<ValueType>, 
    status: Streams.Status,
    client?: Clients.SetClient<ValueType>
} {
    const { zone } = React.useContext(Database.Context);
    const [ values, setValues ] = React.useState<Set<ValueType>>(new Set());
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true });
    const client = Clients.SetClient.fromDelta<ValueType, DomainType>(`${__moduleName__}/useSetProperty(${stream})`, setValues, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
            setSubscription(undefined);
        }
        if(stream) {
            setStatus(_status => ({ ..._status, synchronizing: true }));
            setSubscription(client.watchParent(stream));
            stream.pull && stream.pull().then(() => {
                setStatus(_status => ({ ..._status, initializing: false, synchronizing: false }));
            })
        }
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ stream ]);
    return { domain: stream?.domain, values, status, client: stream?.stateful() };
}

export function useSequenceProperty<ValueType, DomainType extends Elevated.Domain<ValueType>>(
    stream: undefined|Streams.SequenceStream<ValueType, DomainType>
): {
    domain?: DomainType,
    values: Array<ValueType>, 
    status: Streams.Status,
    client?: Clients.SequenceClient<ValueType>
} {
    const { zone } = React.useContext(Database.Context);
    const [ values, setValues ] = React.useState<Array<ValueType>>([]);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true });
    const client = Clients.SequenceClient.fromDelta<ValueType, DomainType>(`${__moduleName__}/useSequenceProperty(${stream})`, setValues, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
            setSubscription(undefined);
        }
        if(stream) {
            setStatus(_status => ({ ..._status, synchronizing: true }));
            setSubscription(client.watchParent(stream));
            stream.pull && stream.pull().then(() => {
                setStatus(_status => ({ ..._status, initializing: false, synchronizing: false }));
            })
        }
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ stream ]);
    return { domain: stream?.domain, values, status, client: stream?.stateful() };
}

export function useRelation<RelationTypeParams extends Objects.BindingType>(
    stream: undefined|Streams.RelationStream<RelationTypeParams>
): {
    entries: RelationTypeParams[],
    status: Streams.Status,
    client?: Clients.RelationClient<RelationTypeParams>
} {
    const { zone } = React.useContext(Database.Context);
    const [ entries, setEntries ] = React.useState<RelationTypeParams[]>([]);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true });
    const client = Clients.RelationClient.fromDelta(`${__moduleName__}/useRelation(${stream})`, setEntries, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        if(stream) {
            setStatus(_status => ({ ..._status, synchronizing: true }));
            setSubscription(client.watchParent(stream));
            stream.pull && stream.pull().then(() => {
                setStatus(_status => ({ ..._status, initializing: false, synchronizing: false }));
            })
        }
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ stream ]);
    const seen = new Set<string>();
    const entries_: RelationTypeParams[] = [];

    entries.forEach(entry => {
        const keys = [... Object.keys(entry)];
        keys.sort();
        const asString = keys.reduce((_asString, key) => (
            !entry[key].objectId ? _asString : (_asString === "" ? "" : _asString+',')+key+"="+entry[key].objectId
        ), "");
        if(!seen.has(asString)) {
            seen.add(asString);
            entries_.push(entry)
        }
    })
    return { entries: Objects.cmp_arrays(entries, entries_) === 0 ? entries : entries_, status, client: stream?.stateful() };
}

export function useProperties<Streams extends { [streamName:string]: Streams.PropertyStream<any>}>(streams: Streams) {
    return Object.keys(streams).reduce<{
        [name in keyof Streams]?: {
            scalar: ReturnType<typeof useScalarProperty>,
            set: ReturnType<typeof useSetProperty>,
            sequence: ReturnType<typeof useSequenceProperty>
        }
    }>((result, streamName) => (
        { ...result, [streamName]: {
            scalar: !streams[streamName].scalar ? undefined : useScalarProperty(streams[streamName].scalar),
            set: !streams[streamName].set ? undefined : useSetProperty(streams[streamName].set),
            sequence: !streams[streamName].set ? undefined : useSequenceProperty(streams[streamName].sequence),
        }}
    ), {}) as {
        [name in keyof Streams]: {
            scalar: ReturnType<typeof useScalarProperty>,
            set: ReturnType<typeof useSetProperty>,
            sequence: ReturnType<typeof useSequenceProperty>
        }
    };
}

export function useStream<StreamTypeParams extends Streams.TypeParams>(
    stream: Streams.AnyStream<StreamTypeParams>
) {
    return {
        scalar: !stream.scalar ? undefined : useScalarProperty(stream.scalar),
        set: !stream.set ? undefined : useSetProperty(stream.set),
        sequence: !stream.set ? undefined : useSequenceProperty(stream.sequence),
        relation: !stream.relation ? undefined : useRelation(stream.relation)
    } 
}

export function useStreams<PatternStreams extends { [streamName:string]: Streams.TypeParams }>(
    streams: { [streamName in keyof PatternStreams]: Streams.AnyStream<PatternStreams[streamName]> }
) {
    return Object.keys(streams).reduce<{
        [name in keyof PatternStreams]?: {
            scalar: ReturnType<typeof useScalarProperty<PatternStreams[name]['Value'], Elevated.Domain<PatternStreams[name]['Value']>>>,
            set: ReturnType<typeof useSetProperty<PatternStreams[name]['Value'], Elevated.Domain<PatternStreams[name]['Value']>>>,
            sequence: ReturnType<typeof useSequenceProperty<PatternStreams[name]['Value'], Elevated.Domain<PatternStreams[name]['Value']>>>,
            relation: ReturnType<typeof useRelation<PatternStreams[name]['Binding']>>,
        }
    }>((result, streamName) => (
        { ...result, [streamName]: useStream(streams[streamName]) }
    ), {});
}
